// src/widgets/LearnersPickerModal.jsx
import { useEffect, useMemo, useState, useCallback } from "react"
import { X, LoaderCircle, Search, Users, Briefcase } from "lucide-react"
import Select from "react-select"
import { motion } from "framer-motion"
import ConfirmModal from "@/widgets/ConfirmModal"
import DataTable from "@/widgets/DataTable"
import { getLearners, getGroups, getJobTitles, getPrefixes, addClassMember } from "@/api/kuizy"

export default function LearnersPickerModal({ classId, onSuccess, onClose }) {
  const [groups, setGroups] = useState([])
  const [jobs, setJobs] = useState([])
  const [prefixes, setPrefixes] = useState([])
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState({ open: false, msg: "" })

  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(() => parseInt(localStorage.getItem("dt:classPicker:perPage")) || 10)
  const [sortBy, setSortBy] = useState("first_name")
  const [sortDir, setSortDir] = useState("asc")

  const [groupF, setGroupF] = useState({ value: "", label: "ทุกกลุ่มผู้เรียน" })
  const [jobF, setJobF] = useState({ value: "", label: "ทั้งหมด" })
  const [searchIn, setSearchIn] = useState("")
  const [search, setSearch] = useState("")

  const [selectedMap, setSelectedMap] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => { localStorage.setItem("dt:classPicker:perPage", String(perPage)) }, [perPage])

  useEffect(() => {
    Promise.all([getGroups(), getJobTitles(1), getPrefixes()]).then(([g, j, pf]) => {
      setGroups(g.data || [])
      setJobs(j.data || [])
      setPrefixes(pf.data || [])
    })
  }, [])

  const fullName = useCallback(u => {
    const pre =
      prefixes.find(p => p.id === u.prefix_id)?.name_th ||
      prefixes.find(p => p.code === u.prefix)?.name_th ||
      u.prefix || ""
    const fn = u.first_name || u.name || ""
    const ln = u.last_name || ""
    return `${pre ? pre + " " : ""}${fn} ${ln}`.trim()
  }, [prefixes])

  const groupOpts = useMemo(
    () => [{ value: "", label: "ทุกกลุ่มผู้เรียน" }, ...groups.filter(g => g.id !== "member").map(g => ({ value: String(g.id), label: g.name }))],
    [groups]
  )
  const jobOpts = useMemo(
    () => [{ value: "", label: "ทั้งหมด" }, ...jobs.map(j => ({ value: j.job_title, label: `${j.job_title} (${j.cnt})` }))],
    [jobs]
  )

  const load = useCallback(() => {
    setLoading(true)
    getLearners({
      status: "1",
      searchText: search || undefined,
      group_id: groupF.value || undefined,
      job_title: jobF.value || undefined,
      page,
      per_page: perPage,
      sort_by: sortBy,
      sort_dir: sortDir,
      exclude_class_id: classId
    })
      .then((res) => {
        const d = res?.data || {}
        const arr = Array.isArray(d?.rows) ? d.rows
          : Array.isArray(d) ? d
            : Array.isArray(d?.data) ? d.data
              : []
        setRows(arr || [])
        setTotal(Number(d?.total || (Array.isArray(arr) ? arr.length : 0)))
      })
      .catch(() => setAlert({ open: true, msg: "โหลดข้อมูลไม่สำเร็จ" }))
      .finally(() => setLoading(false))
  }, [search, groupF, jobF, page, perPage, sortBy, sortDir, classId])

  useEffect(load, [load])

  const selectedSet = useMemo(() => new Set(Object.keys(selectedMap).filter(k => selectedMap[k])), [selectedMap])
  const rowKey = u => `learner_${u.id}`

  const onToggleRow = (key) => {
    setSelectedMap(s => {
      const n = { ...s }
      if (n[key]) delete n[key]
      else n[key] = true
      return n
    })
  }

  const onToggleAll = (keysOnPage) => {
    const all = keysOnPage.every(k => selectedSet.has(k))
    setSelectedMap(s => {
      const n = { ...s }
      if (all) keysOnPage.forEach(k => delete n[k])
      else keysOnPage.forEach(k => (n[k] = true))
      return n
    })
  }

  const save = async () => {
    const ids = Array.from(selectedSet).map(k => k.split("_")[1])
    if (ids.length === 0) return setAlert({ open: true, msg: "ยังไม่ได้เลือกผู้เรียน" })
    setSaving(true)
    try {
      const tasks = ids.map(id => addClassMember({ class_id: classId, learner_id: id }))
      await Promise.all(tasks)
      onSuccess && onSuccess()
    } catch {
      setAlert({ open: true, msg: "บันทึกไม่สำเร็จ" })
    } finally {
      setSaving(false)
    }
  }

  const columns = useMemo(() => [
    { key: "full_name", title: "ชื่อ–สกุล", sortable: true, sortKey: "first_name", render: (u) => fullName(u) },
    { key: "group_names", title: "กลุ่มผู้เรียน", render: (u) => u.group_names?.length ? u.group_names.join(", ") : "Member" },
    { key: "job_title", title: "ตำแหน่งงาน", sortable: true, render: (u) => u.job_title || "-" },
    { key: "email", title: "อีเมล", sortable: true },
    { key: "phone", title: "โทรศัพท์", render: (u) => ((u.phone_code || "") + (u.phone || "")) || "-" },
  ], [fullName])

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
      <div className="w-full h-full sm:h-[90vh] sm:w-[90%] bg-white rounded-lg shadow-lg flex flex-col overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-info">
          <h3 className="font-semibold text-sm sm:text-base">เพิ่มผู้เรียนเข้าห้องเรียน</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-primary"><X size={20} /></button>
        </header>

        <div className="flex flex-wrap items-end gap-3 px-4 py-2 bg-info border-b border-gray-200">
          <div className="flex flex-col gap-1 w-48">
            <label className="flex items-center gap-1 text-sm"><Users size={16} /> กลุ่มผู้เรียน</label>
            <Select classNamePrefix="rs" options={groupOpts} value={groupF} onChange={o => { setGroupF(o); setPage(1) }} isSearchable />
          </div>
          <div className="flex flex-col gap-1 w-52">
            <label className="flex items-center gap-1 text-sm"><Briefcase size={16} /> ตำแหน่งงาน</label>
            <Select classNamePrefix="rs" options={jobOpts} value={jobF} onChange={o => { setJobF(o); setPage(1) }} isSearchable />
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
            <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <div className="flex items-center gap-2">
                <input value={searchIn} onChange={e => setSearchIn(e.target.value)} onKeyDown={e => e.key === "Enter" && (setSearch(searchIn), setPage(1))} placeholder="ค้นหาชื่อ / อีเมล / โทรศัพท์" className="w-full border border-gray-300 rounded-lg pl-10 px-3 py-2 text-sm focus:ring-2 focus:ring-primary" />
                <button onClick={() => { setSearch(searchIn); setPage(1) }} className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm">
                  <Search size={16} /> ค้นหา
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 min-h-0 p-3">
          <DataTable
            className="h-full min-h-0"
            columns={columns}
            rows={rows}
            loading={loading}
            total={total}
            page={page}
            perPage={perPage}
            sortBy={sortBy}
            sortDir={sortDir}
            onPageChange={setPage}
            onPerPageChange={(n) => { setPerPage(n); setPage(1) }}
            onSortChange={(k, d) => { setSortBy(k); setSortDir(d); setPage(1) }}
            selectable
            rowKey={rowKey}
            selectedSet={selectedSet}
            onToggleRow={onToggleRow}
            onToggleAll={onToggleAll}
          />
        </div>

        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">เลือกแล้ว {selectedSet.size} รายการ</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-300 text-sm">ยกเลิก</button>
            <button onClick={save} disabled={selectedSet.size === 0 || saving} className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50">
              {saving && <LoaderCircle size={16} className="animate-spin" />} เพิ่มผู้เรียน
            </button>
          </div>
        </div>

        {alert.open && <ConfirmModal type="alert" title="แจ้งเตือน" message={alert.msg} onClose={() => setAlert({ open: false, msg: "" })} />}
      </div>
    </motion.div>
  )
}
